import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { useGraphql } from 'utils/hooks/useGraphql';
import { isNonEmptyArray, isNotNull } from 'utils/tsHelpers';

import { JOURNEY_METADATA_FRAGMENT } from './constants';
import { JourneyMetadata } from './types';
import { dedupeBy } from '../../../utils/dedupeBy';

const createQueryForJourneyId = (journeyId: number) => `
  published_journey_${journeyId}:workflow(id:${journeyId}, workflowType:Published) {
    ...JourneyMetadata
  }
  draft_journey_${journeyId}:workflow(id:${journeyId}, workflowType:Draft) {
    ...JourneyMetadata
  }
`;

/**
 *
 * Note: "createQueryForJourneyIds" won't create a valid query if journeyIds is empty.
 * We also can't skip the gql call unless the query itself is valid.
 * To resolve this we can pass an array with a psuedo id (ie: [0]) to satisfy the query in order to skip
 * the gql call. The actual ids doesn't really matter since it will be skipped.
 */

const createQueryForJourneyIds = (journeyIds: number[]) => gql`
  ${JOURNEY_METADATA_FRAGMENT}

  query JourneysById {
    ${journeyIds.sort().map(createQueryForJourneyId).join('\n')}
  }
`;

const emptyQueryIds = [0];

export const useJourneysByIds = (journeyIds: number[]) => {
  const query = createQueryForJourneyIds(isNonEmptyArray(journeyIds) ? journeyIds : emptyQueryIds);
  const {
    data: results,
    error: didError,
    isLoading,
  } = useGraphql<
    Record<`published_journey_${number}` | `draft_journey_${number}`, JourneyMetadata | null>
  >(query, { skip: !isNonEmptyArray(journeyIds) });
  const journeys = useMemo(
    () => dedupeBy(Object.values(results ?? {}).filter(isNotNull), 'id'),
    [results],
  );
  return { journeys, didError, isLoading };
};
